import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Settings, Utensils, Ticket, Film, Bed, Building, ClipboardList, Plus } from 'lucide-react';
import { api } from '@/lib/api';
import type { VenueBookingType } from '@/types';

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  utensils: Utensils,
  ticket: Ticket,
  film: Film,
  bed: Bed,
  building: Building,
  clipboard: ClipboardList,
};

export default function BookingConfig() {
  const { venueId } = useParams();
  const navigate = useNavigate();

  const { data: venue } = useQuery({
    queryKey: ['venue', venueId],
    queryFn: async () => {
      const response = await api.get(`/admin/venues/${venueId}`);
      return response.data;
    },
  });

  const { data: bookingTypes, isLoading } = useQuery<VenueBookingType[]>({
    queryKey: ['venue-booking-types', venueId],
    queryFn: async () => {
      const response = await api.get(`/admin/venues/${venueId}/booking-types`);
      return response.data;
    },
  });

  const { data: areas } = useQuery({
    queryKey: ['venue-areas', venueId],
    queryFn: async () => {
      const response = await api.get(`/admin/venues/${venueId}/areas`);
      return response.data;
    },
  });

  const { data: forms } = useQuery({
    queryKey: ['venue-forms', venueId],
    queryFn: async () => {
      const response = await api.get(`/admin/venues/${venueId}/forms`);
      return response.data;
    },
  });

  const { data: screens } = useQuery({
    queryKey: ['cinema-screens', venueId],
    queryFn: async () => {
      const response = await api.get(`/admin/venues/${venueId}/cinema/screens`);
      return response.data;
    },
  });

  const getCountForType = (typeId: string, slug?: string) => {
    if (slug === 'general-requests') {
      return { count: (forms as unknown[])?.length || 0, label: 'Forms created:' };
    }
    if (slug === 'cinema-seats') {
      return { count: (screens as unknown[])?.length || 0, label: 'Screens configured:' };
    }
    if (!areas) return { count: 0, label: 'Areas configured:' };
    return {
      count: (areas as Record<string, unknown>[]).filter(a => a.booking_type_id === typeId).length,
      label: 'Areas configured:'
    };
  };

  const getConfigLink = (venueId: string, slug?: string, typeId?: string) => {
    switch (slug) {
      case 'general-requests':
        return `/venues/${venueId}/forms`;
      case 'cinema-seats':
        return `/venues/${venueId}/cinema`;
      default:
        return `/venues/${venueId}/booking-config/areas?type=${typeId}`;
    }
  };

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/venues')}
          className="p-2 hover:bg-gray-100 rounded-md"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Booking Configuration</h1>
          <p className="text-sm text-gray-500">{(venue as Record<string, unknown>)?.name as string}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {isLoading ? (
          [...Array(6)].map((_, i) => (
            <div key={i} className="card p-6 animate-pulse">
              <div className="h-12 w-12 bg-gray-200 rounded-lg mb-4"></div>
              <div className="h-5 bg-gray-200 rounded w-32 mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-48"></div>
            </div>
          ))
        ) : (
          bookingTypes?.map((type) => {
            const Icon = iconMap[type.icon || 'clipboard'] || ClipboardList;
            const { count, label } = getCountForType(type.id, type.slug);
            const isEnabled = type.is_enabled;

            return (
              <div
                key={type.id}
                className={`card p-6 ${!isEnabled ? 'opacity-50' : ''}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="p-3 bg-primary-100 rounded-lg">
                    <Icon className="h-6 w-6 text-primary-600" />
                  </div>
                  {isEnabled && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Enabled
                    </span>
                  )}
                </div>

                <h3 className="text-lg font-semibold text-gray-900 mb-1">{type.name}</h3>
                <p className="text-sm text-gray-500 mb-4">{type.description}</p>

                {isEnabled ? (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">{label}</span>
                      <span className="font-medium">{count}</span>
                    </div>
                    <Link
                      to={getConfigLink(venueId!, type.slug, type.id)}
                      className="btn btn-primary w-full"
                    >
                      <Settings className="h-4 w-4 mr-2" />
                      Configure
                    </Link>
                  </div>
                ) : (
                  <button
                    className="btn btn-secondary w-full"
                    onClick={async () => {
                      await api.post(`/admin/venues/${venueId}/booking-types/${type.id}`, {
                        isEnabled: true,
                      });
                      window.location.reload();
                    }}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Enable
                  </button>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
